/**
 * The pin on the web side: the write both menus share, and the mark a pinned
 * project wears.
 *
 * One file because two screens need both and neither owns them - the row menu in
 * the register pins a project the operator noticed while scanning, the project
 * page pins the one they opened on purpose. `ProjectArchive.tsx` next door is
 * the same arrangement for the same reason, and this file follows its shape
 * deliberately so the two read alike.
 *
 * ── NO DIALOG, IN EITHER DIRECTION ────────────────────────────────────────
 *
 * Archiving asks a question first, because its consequence is invisible from
 * where the decision is made: the project vanishes from pickers the operator is
 * not looking at. Pinning has no such hidden cost. Nothing about the project
 * changes, no other operator sees anything, and the result is visible in the
 * same breath - the project appears in the strip above the register. A confirm
 * here would be a dialog guarding a bookmark.
 *
 * ── WHY THE HOOK RETURNS A `busy` FLAG ────────────────────────────────────
 *
 * The server is idempotent in both directions, so a double click is harmless to
 * the data. It is not harmless to the operator: a menu entry that can be pressed
 * twice while the first press is in flight looks broken whether or not it is,
 * and the second press would fire against a state the first one is still
 * changing. Same reason `useUnarchiveProject` carries one.
 */
import { useState } from "react";
import { Pin } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { pinProject, unpinProject } from "../../../lib/projectPins";
import type { Project } from "../../../lib/projects";
import { ApiError } from "../../../lib/api";
import { StatusPill, useToast } from "../../ui";

/**
 * The mark a pinned project wears wherever it is drawn.
 *
 * Muted, not coloured, for the same reason `ArchivedBadge` is: this is not a
 * lifecycle STATUS - the project is still `in_progress` or `completed`
 * underneath, and the pill beside this one keeps saying so. A second coloured
 * pill would have two badges arguing about the same row.
 *
 * It reads `project.pinned`, which is per-operator and absent on rows that came
 * from an endpoint that does not attach it. Absent is drawn as not pinned: a
 * badge is a claim, and there is nothing here to claim.
 */
export function PinnedBadge({ project }: { project: Project }) {
  const t = useT();
  if (!project.pinned) return null;
  return (
    <StatusPill tone="mut">
      <Pin className="size-3" />
      {t("projects.pinnedBadge")}
    </StatusPill>
  );
}

/**
 * Pin or unpin, with the receipt and the busy flag the menus are missing.
 *
 * `onDone` is handed the state the project is now IN, taken from the server's
 * answer rather than from what was asked for. The two agree today, and the day
 * they stop agreeing - a limit refusal, a race with another tab - the screen
 * should follow the server rather than the click.
 */
export function usePinProject(): {
  busy: boolean;
  run: (project: Project, onDone: (pinned: boolean) => void) => Promise<void>;
} {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  return {
    busy,
    run: async (project, onDone) => {
      if (busy) return;
      setBusy(true);
      const wantPinned = !project.pinned;
      try {
        const res = wantPinned ? await pinProject(project.id) : await unpinProject(project.id);
        onDone(res.pinned);
        toast(t(res.pinned ? "projects.pinned" : "projects.unpinned"));
      } catch (e) {
        // The one refusal worth naming: the shortlist is full. A generic error
        // there would send the operator looking for a network problem, when what
        // they have to do is unpin something.
        if (e instanceof ApiError && e.status === 409) {
          const limit = (e.body as { limit?: number } | null)?.limit;
          toast(t("projects.pinLimit", { n: limit ?? 0 }));
        } else {
          toast(t("common.error"));
        }
      } finally {
        setBusy(false);
      }
    },
  };
}
